import { forwardRef, type ReactNode, type Ref, type AnchorHTMLAttributes } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import './Anchor.css';

export interface AnchorProps extends BaseProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color'> {
  children?: ReactNode;
  /**
   * Underline behaviour. Defaults to `never`: hover is signalled by the
   * cursor and a colour shift, not by text that moves under the pointer.
   * Pass `always` for a link sitting inside a paragraph, where the underline
   * is the only thing marking it as a link.
   */
  underline?: 'always' | 'hover' | 'never';
}

/** Themed link element. */
export const Anchor = forwardRef(function Anchor(
  { underline = 'never', children, target, rel, ...rest }: AnchorProps,
  ref: Ref<HTMLAnchorElement>
): ReactNode {
  const base = useBaseAttrs('rr-anchor', rest, [`rr-anchor--underline-${underline}`]);
  const { dom } = splitBaseProps(rest);
  // Auto-attach rel for safety when target=_blank.
  const safeRel = target === '_blank' ? (rel ?? 'noopener noreferrer') : rel;
  return (
    <a {...dom} ref={ref} target={target} rel={safeRel} {...base.attrs} className={base.className} style={base.style}>
      {children}
      {target === '_blank' ? <span className="rr-sr-only"> (opens in new tab)</span> : null}
    </a>
  );
});
